import { registerInputRefFactory } from '@modules/admin-generic/admin-generic.utils'
import LinkStatus from '@modules/admin-requirements/link-status'
import { useGenerateParagraphLinks } from '@modules/admin-requirements/use-generate-paragraph-links'
import {
  Autocomplete,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField as MaterialTextField,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { BackdropProgress } from '@shared/components/backdrop-progress.component'
import { ErrorsAlert } from '@shared/components/errors-alert.component'
import { originalIcons } from '@shared/components/icons/icons'
import { InfoTooltip } from '@shared/components/info-tooltip.component'
import { ProgressButton } from '@shared/components/progress-button.component'
import { TextField } from '@shared/components/text-field.component'
import {
  LegalActStrings,
  NO_OPTIONS_PLACEHOLDER,
  RequirementForms,
} from '@shared/consts'
import { useHandleResourceState } from '@shared/hooks/use-handle-resource-state.hook'
import { useRouter } from '@shared/hooks/use-router'
import { useScopesQuery } from '@shared/hooks/use-scopes-query.hook'
import { useThemeStyles } from '@shared/theme/use-theme-styles'
import {
  BaseRequirement,
  BaseRequirementLegalBasis,
  LegalBasis,
  RequirementAlert,
  RequirementForm,
  RequirementGroup,
  Scope,
} from '@shared/types'
import { apiJSON } from '@shared/utils/api/api.util'
import { isEqual } from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import { Controller, FormProvider, useFieldArray } from 'react-hook-form'
import { useQuery } from 'react-query'
import { Navigate } from 'react-router-dom'
import { adminRequirementTooltips } from './consts'
import { RequirementMappingTree } from './requirement-mapping-tree.component'
import { RequirementCalendarDialog } from './requirement-calendar.dialog.component'
import { AlertComponent } from './alert.component'

export enum SAVE_TYPES {
  DEPRECATE = 'Deprecate',
  SILENT = 'Silent',
  NORMAL = 'Normal',
}

const PARAGRAPH_INPUT_INFO_DESCRIPTION = (
  <div>
    <p>Tutaj należy wpisać odpowiednie artykuły lub paragrafy dokumentu:</p>
    <ul>
      <li>
        Wpisuj artykuły/paragrafy/załączniki lub inną treść po przecinku, np.:
        Art. 4, Art. 6, Zał. 2 itp. Dzięki temu system je prawidłowo odczyta.
      </li>
      <li>
        Aby dodać pojedynczy artykuł, wpisz: <code>art. 1</code>
      </li>
      <li>
        Aby dodać przypis górny dodaj znak <code>^</code> np.:{' '}
        <code>art. 9^1, Art. 11^3</code>
      </li>
      <li>
        Aby dodać wiele artykułów, oddziel je przecinkami, np.:{' '}
        <code>art. 1, art. 2, art. 3</code>
      </li>
      <li>
        Aby dodać paragraf, użyj znaku §, np.: <code>§ 1</code>
      </li>
      <li>
        Aby dodać wiele paragrafów, oddziel je przecinkami, np.:{' '}
        <code>§ 1, § 2, § 3</code>
      </li>
      <li>
        Aby dodać zakres artykułów/paragrafów, wpisz numer pierwszego i
        ostatniego w zakresie, oddzielając je myślnikiem:{' '}
        <code>Art. 1-20, § 1-20</code>
      </li>
    </ul>
  </div>
)

export const AdminRequirementCrudDialog = () => {
  const classes = useStyles()
  const sharedClasses = useThemeStyles()

  const { navigate, query } = useRouter<{ resourceID?: string }>()

  const { areScopesLoading, scopes } = useScopesQuery()
  const [currentScope, setCurrentScope] = useState<Scope | null>(null)
  const [scopeGroups, setScopeGroups] = useState<
    { groupID: number; name: string }[]
  >([])
  const [isCalendarDialogOpen, setIsCalendarDialogOpen] = useState(false)
  const { legalBasisLinks, handleGenerateLinks } = useGenerateParagraphLinks()
  const { data: groups, isLoading: isLoadingGroups } = useQuery<
    RequirementGroup[]
  >('groups', () => apiJSON('groups/crud'))
  const { data: legalBasisList, isLoading: isLoadingLegalBasis } = useQuery<
    LegalBasis[]
  >('legalbasis/crud', () => apiJSON('legalbasis/crud'))

  const onScopeChange = useCallback(
    (_e: any, value: Scope | null) => {
      if (value) {
        setCurrentScope(value)
      }
    },
    [setCurrentScope]
  )

  const {
    formMethods,
    onAddResource,
    mutation: {
      isResourceEdit,
      isResourceCreating,
      isResourceCreationSucceed,
      createResourceErrors,
      resetResourceCreationState,
    },
    resourceData,
  } = useHandleResourceState<
    BaseRequirement & {
      saveMethod: SAVE_TYPES
      companiesIds: number[]
      alert: RequirementAlert
    }
  >({
    resourceEndpoint: 'requirementbase/crud',
    resourceID: query?.resourceID,
  })

  const {
    fields: legalBasisFields,
    append: addLegalBasis,
    remove: removeLegalBasis,
  } = useFieldArray<{ legalBasis: any[] }>({
    control: formMethods.control,
    name: 'legalBasis',
  })

  const {
    fields: forms,
    append: addForm,
    remove: removeFrom,
  } = useFieldArray<{ forms: { title: string; lexUrl: string }[] }>({
    control: formMethods.control,
    name: 'forms',
  })

  const {
    formState: { isValid },
  } = formMethods

  const legalBasisValue = formMethods.watch('legalBasis')
  const isPublishedFormValue = formMethods.watch('isPublished')
  const companiesIds = formMethods.watch('companiesIds')

  const { setValue: setFormValue } = formMethods

  useEffect(() => {
    setScopeGroups(
      (groups || [])
        .filter((group) => group.scope.scopeID === currentScope?.scopeID)
        .map((group) => ({ groupID: group?.groupID, name: group.name }))
    )
  }, [currentScope, setFormValue, groups])

  const onLegalBasisRemoveFactory = useCallback(
    (removedLegalBasisIndex: number) => () => {
      removeLegalBasis(removedLegalBasisIndex)
    },
    [removeLegalBasis]
  )

  const onFormRemoveFactory = useCallback(
    (removeFormIndex: number) => () => {
      removeFrom(removeFormIndex)
    },
    [removeFrom]
  )

  const onLegalBasisAdd = useCallback(() => {
    if (legalBasisList) {
      addLegalBasis({
        paragraph: '',
        legalBasis: {
          ...legalBasisList[0],
        },
      })
    }
  }, [legalBasisList])

  const onFormAdd = useCallback(() => {
    addForm({
      title: '',
      lexUrl: '',
    })
  }, [])

  const onCompaniesMappingChange = (companiesIDs: number[]) => {
    setFormValue('companiesIds', companiesIDs)
  }

  const registerInputRef = registerInputRefFactory(formMethods.register)

  useEffect(() => {
    if (!legalBasisLinks.length) {
      handleGenerateLinks(legalBasisValue)
    }
  }, [legalBasisValue])

  useEffect(() => {
    if (isResourceEdit && resourceData) {
      setCurrentScope(resourceData.scope)
    }
  }, [isResourceEdit, resourceData])

  if (isResourceCreationSucceed) {
    return <Navigate to="/admin/requirements" />
  }

  const setSaveMethodAndAddResource = () => {
    let alertId = null
    const alert = formMethods.watch('alert')

    if (!isPublishedFormValue) {
      setFormValue('alert', null)
      setFormValue('companiesIds', [])
    }

    if (isResourceEdit) {
      if (!formMethods.watch('alertOverride') && alert?.id) {
        alertId = resourceData?.alert?.id
      }
    }

    onAddResource({
      ...(!isResourceEdit
        ? { groupID: formMethods.getValues('group')?.groupID }
        : {}),
      ...(isResourceEdit && alert
        ? {
            alert: {
              ...alert,
              id: alertId,
            },
          }
        : {}),
      legalBasis: formMethods
        .getValues('legalBasis')
        .map((item: BaseRequirementLegalBasis) => ({
          legalBasisId: item.legalBasis.legalBasisID,
          paragraph: item.paragraph,
        })),
      forms: formMethods.getValues('forms').map((item: RequirementForm) => ({
        title: item.title,
        lexUrl: item.lexUrl,
      })),
    })
  }

  const handleCreateAlert = async (date: Date) => {
    setIsCalendarDialogOpen(false)
    setFormValue('alert', {
      id: resourceData?.alert?.id ?? null,
      expirationDate: date.toISOString(),
    })
  }

  const handleRemoveAlert = () => {
    setFormValue('alert', null)
  }

  return !areScopesLoading && !isLoadingLegalBasis && !isLoadingGroups ? (
    <>
      <Dialog open fullWidth maxWidth="md" onClose={() => navigate(-1)}>
        <form noValidate>
          <FormProvider {...formMethods}>
            <DialogTitle className={sharedClasses.dialogTitle}>
              {isResourceEdit ? 'Edytuj' : 'Utwórz nowy'}
            </DialogTitle>
            <DialogContent dividers>
              <ErrorsAlert
                errors={createResourceErrors}
                onClose={resetResourceCreationState}
              />
              <Autocomplete
                value={currentScope as Scope}
                className={sharedClasses.autocomplete}
                autoHighlight
                options={scopes!}
                getOptionLabel={(option) => option?.name ?? ''}
                isOptionEqualToValue={isEqual}
                renderInput={(params) => (
                  <MaterialTextField
                    {...params}
                    label="Zakres"
                    variant="outlined"
                  />
                )}
                onChange={onScopeChange}
                filterSelectedOptions
                disableClearable
                disabled={isResourceEdit}
                defaultValue={null as unknown as Scope}
                noOptionsText={NO_OPTIONS_PLACEHOLDER}
              />
              <Controller
                name="group"
                control={formMethods.control}
                render={(formControllerProps) => {
                  const value = formControllerProps.field.value
                  const currentGroup = value?.groupID
                    ? scopeGroups.find(
                        (group) => group?.groupID === value?.groupID
                      )
                    : value
                  return (
                    <Autocomplete
                      value={currentGroup}
                      className={sharedClasses.autocomplete}
                      autoHighlight
                      options={scopeGroups}
                      getOptionLabel={(option: any) => option?.name}
                      getOptionKey={(option: any) => option?.groupID}
                      isOptionEqualToValue={isEqual}
                      renderInput={(params) => (
                        <MaterialTextField
                          {...params}
                          label="Grupa"
                          variant="outlined"
                        />
                      )}
                      onChange={(_e, data) =>
                        formControllerProps.field.onChange(data)
                      }
                      filterSelectedOptions
                      disableClearable
                      disabled={isResourceEdit || !currentScope}
                      {...formControllerProps}
                      noOptionsText={NO_OPTIONS_PLACEHOLDER}
                    />
                  )
                }}
                defaultValue={null}
              />
              <TextField
                {...registerInputRef('name', { required: true })}
                required
                label="Nazwa wymagania"
              />
              <TextField
                {...registerInputRef('description', {
                  required: true,
                })}
                required
                multiline
                label="Wymaganie prawne"
              />
              <TextField
                {...registerInputRef('tooltipData')}
                multiline
                label="Objaśnienia"
              />
              <TextField
                {...registerInputRef('fixDate')}
                multiline
                label="Termin realizacji"
              />
              <TextField
                {...registerInputRef('order', { required: true })}
                required
                type="number"
                label="Pozycja"
              />
              <Card variant="outlined" className={classes.legalBasisList}>
                <Typography color="textSecondary">Podstawy prawne</Typography>
                <IconButton
                  color="primary"
                  aria-label="add legal basis"
                  component="span"
                  className={classes.addLegalBasis}
                  onClick={onLegalBasisAdd}
                  size="large"
                >
                  <originalIcons.Add />
                </IconButton>
                <CardContent>
                  {legalBasisFields.map((item, index) => {
                    const { legalBasis, paragraph } =
                      item as unknown as BaseRequirementLegalBasis
                    const { legalBasisID } = legalBasis

                    if (legalBasisID) {
                      return (
                        <Card
                          variant="outlined"
                          className={classes.legalBasisItem}
                          key={`${legalBasisID}.${index}`}
                        >
                          <IconButton
                            color="primary"
                            aria-label="remove legal basis"
                            component="span"
                            className={classes.removeLegalBasis}
                            onClick={onLegalBasisRemoveFactory(index)}
                            size="large"
                          >
                            <originalIcons.Clear />
                          </IconButton>
                          <CardContent>
                            <Controller
                              name={`legalBasis[${index}].legalBasis`}
                              control={formMethods.control}
                              defaultValue={legalBasisID}
                              render={(formControllerProps) => {
                                const filteredLegalBasisList: LegalBasis[] =
                                  legalBasisList?.filter((item) =>
                                    Boolean(item.documentName)
                                  ) as LegalBasis[]

                                return (
                                  <Autocomplete
                                    key={`legal-basis-${index}`}
                                    renderOption={(props, option) => (
                                      <li
                                        {...props}
                                        key={`${option.legalBasisID}-${option.documentName}`}
                                      >
                                        {option.documentName}
                                      </li>
                                    )}
                                    value={formControllerProps.field.value}
                                    forcePopupIcon={false}
                                    className={sharedClasses.autocomplete}
                                    autoHighlight
                                    options={
                                      filteredLegalBasisList as LegalBasis[]
                                    }
                                    getOptionLabel={(option: LegalBasis) =>
                                      option?.documentName ?? '[USUNIĘTE]'
                                    }
                                    isOptionEqualToValue={isEqual}
                                    renderInput={(params) => (
                                      <MaterialTextField
                                        {...params}
                                        InputProps={{
                                          ...(params.InputProps ?? {}),
                                          endAdornment: (
                                            <InputAdornment position="end">
                                              {formControllerProps.field.value
                                                ?.legalBasisType?.name ??
                                                'brak typu'}
                                            </InputAdornment>
                                          ),
                                        }}
                                        label={LegalActStrings.publisherNumber}
                                        variant="outlined"
                                      />
                                    )}
                                    onChange={(_e, data) => {
                                      formControllerProps.field.onChange(
                                        data ?? null
                                      )
                                    }}
                                    filterSelectedOptions
                                    {...formControllerProps}
                                    noOptionsText={NO_OPTIONS_PLACEHOLDER}
                                  />
                                )
                              }}
                            />
                            <TextField
                              {...registerInputRef(
                                `legalBasis[${index}].paragraph`
                              )}
                              multiline
                              label={LegalActStrings.legalArticle}
                              defaultValue={paragraph}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault()
                                }
                              }}
                              onBlur={(e) => {
                                registerInputRef(
                                  `legalBasis[${index}].paragraph`
                                )?.onBlur(e)
                                handleGenerateLinks(legalBasisValue)
                              }}
                              InputProps={{
                                endAdornment: (
                                  <>
                                    <InfoTooltip
                                      interactive
                                      description={
                                        PARAGRAPH_INPUT_INFO_DESCRIPTION
                                      }
                                    />
                                  </>
                                ),
                                style: {
                                  padding: '13px 14px',
                                },
                              }}
                            />
                            <Card variant="outlined">
                              <CardContent>
                                {legalBasisLinks
                                  .find(
                                    ({ index: legalBasisIndex }) =>
                                      index === legalBasisIndex
                                  )
                                  ?.links?.map((link) => (
                                    <LinkStatus key={link} link={link} />
                                  ))}
                              </CardContent>
                            </Card>
                          </CardContent>
                        </Card>
                      )
                    }
                    return null
                  })}
                </CardContent>
              </Card>
              <Card variant="outlined" className={classes.formList}>
                <Typography color="textSecondary">Formularze</Typography>
                <IconButton
                  color="primary"
                  aria-label="add form"
                  component="span"
                  className={classes.addForm}
                  onClick={onFormAdd}
                  size="large"
                >
                  <originalIcons.Add />
                </IconButton>
                <CardContent>
                  {forms.map((item, index) => {
                    const { title, lexUrl } = item

                    if (item) {
                      return (
                        <Card
                          variant="outlined"
                          className={classes.legalBasisItem}
                          key={`forms[${index}]`}
                        >
                          <IconButton
                            color="primary"
                            aria-label="remove form"
                            component="span"
                            className={classes.removeForm}
                            onClick={onFormRemoveFactory(index)}
                            size="large"
                          >
                            <originalIcons.Clear />
                          </IconButton>
                          <CardContent>
                            <TextField
                              {...registerInputRef(`forms[${index}].title`)}
                              multiline
                              label={RequirementForms.title}
                              defaultValue={title}
                            />
                            <TextField
                              {...registerInputRef(`forms[${index}].lexUrl`)}
                              multiline
                              label={RequirementForms.lexUrl}
                              defaultValue={lexUrl}
                            />
                          </CardContent>
                        </Card>
                      )
                    }
                    return null
                  })}
                </CardContent>
              </Card>
              {isPublishedFormValue && (
                <>
                  <RequirementMappingTree
                    companiesIds={(companiesIds as number[]) || []}
                    onCompaniesMappingChange={onCompaniesMappingChange}
                    currentScope={currentScope}
                    isResourceEdit={isResourceEdit}
                    isPublished={resourceData?.isPublished}
                  />
                  <AlertComponent
                    isCalendarDialogOpen={isCalendarDialogOpen}
                    setIsCalendarDialogOpen={setIsCalendarDialogOpen}
                    onRefresh={handleRemoveAlert}
                    isResourceEdit={isResourceEdit}
                  />
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Controller
                control={formMethods.control}
                name="isPublished"
                defaultValue={false}
                render={({ field: { onChange, value } }) => (
                  <FormControlLabel
                    className={classes.isPublishedLabel}
                    color="primary"
                    control={
                      <Tooltip title={adminRequirementTooltips.active}>
                        <div>
                          <Switch
                            onChange={(e) => onChange(e.target.checked)}
                            checked={value}
                            disabled={resourceData?.isPublished}
                          />
                        </div>
                      </Tooltip>
                    }
                    label={
                      <Tooltip title={adminRequirementTooltips.active}>
                        <div>Aktywne</div>
                      </Tooltip>
                    }
                  />
                )}
              />
              <Button
                // autoFocus
                onClick={() => navigate(-1)}
                color="primary"
                variant="outlined"
              >
                Anuluj
              </Button>
              <div>
                <ProgressButton
                  isLoading={isResourceCreating}
                  onClick={() => setSaveMethodAndAddResource()}
                  color="primary"
                  variant="contained"
                  disabled={!isValid}
                >
                  Zapisz
                </ProgressButton>
              </div>
            </DialogActions>
          </FormProvider>
        </form>
      </Dialog>
      <RequirementCalendarDialog
        open={isCalendarDialogOpen}
        onClose={() => setIsCalendarDialogOpen(false)}
        onConfirm={handleCreateAlert}
      />
    </>
  ) : (
    <BackdropProgress />
  )
}

const useStyles = makeStyles(() => {
  return {
    saveMethodRadio: {
      display: 'none',
    },
    addLegalBasis: {
      position: 'absolute',
      top: 0,
      right: 10,
    },
    removeLegalBasis: {
      position: 'absolute',
      right: -10,
      top: -10,
    },
    legalBasisList: {
      marginTop: 15,
      padding: '16px 0px 10px 16px',
      position: 'relative',
    },
    legalBasisItem: {
      position: 'relative',
      marginBottom: 15,
    },
    addForm: {
      position: 'absolute',
      top: 0,
      right: 10,
    },
    removeForm: {
      position: 'absolute',
      right: -10,
      top: -10,
    },
    formList: {
      marginTop: 15,
      padding: '16px 0px 10px 16px',
      position: 'relative',
    },
    formItem: {
      position: 'relative',
      marginBottom: 15,
    },
    isPublishedLabel: {
      flex: 1,
      marginLeft: 5,
    },
  }
})
